import { marked } from "marked";
import React, { useRef, useState, useEffect } from "react";
import BottomBar from "../pages/BottomBar";
import { FiCopy } from "react-icons/fi";


const EditorPanel = ({
    sourceSegments,
    currentIndex,
    isSourceEditing,
    isTargetEditing,
    activeSource,
    activeTarget,
    rtlSource,
    rtlTarget,
    setIsTargetEditing,
    setIsSourceEditing,
    selectSegment,
    goPrev,
    goNext,
    updateSource,
    updateTarget,
    onSaveFile,
}) => {
    const segmentRefs = useRef([]);
    const [search, setSearch] = useState("");
    const targetTextareaRef = useRef(null);
    const total = sourceSegments.length;
    const numStr = `Segment ${currentIndex + 1} of ${total}`;

    const renderPandocImages = (md = "") =>
        md.replace(/!\[([^\]]*)\]\(([^)]+)\)\{([^}]*)\}/g, (_, alt, src, attrs) => {
            let styles = "display:inline; vertical-align:middle;";
            attrs.replace(/(\w+)\s*=\s*"([^"]+)"/g, (_, k, v) => {
                styles += `${k}:${v};`;
            });
            return `<img src="${src}" alt="${alt}" style="${styles}" />`;
        });

    const renderHTML = (md) => ({
        __html: marked.parse(renderPandocImages(md || "")),
    });

    useEffect(() => {
        if (segmentRefs.current[currentIndex]) {
            segmentRefs.current[currentIndex].scrollIntoView({
                behavior: "smooth",
                block: "nearest",
            });
        }
    }, [currentIndex]);

    const handleSave = () => {
        onSaveFile();
        setIsTargetEditing(false);
    };

    const copySourceToTarget = () => {
        const textarea = targetTextareaRef.current;

        if (!textarea) return;

        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;

        const newText =
            activeTarget.substring(0, start) +
            activeSource +
            activeTarget.substring(end);

        updateTarget(newText);

        // restore cursor after inserted text
        setTimeout(() => {
            textarea.focus();

            const cursorPos = start + activeSource.length;

            textarea.selectionStart = cursorPos;
            textarea.selectionEnd = cursorPos;
        }, 0);
    };


    return (
        <>
            <div className="main-layout">
                {/* SIDEBAR */}
                <div className="sidebar">
                    <input
                        id="searchInput"
                        placeholder="Search..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                    <div className="segment-list">
                        {sourceSegments.map((seg, i) =>
                            seg.toLowerCase().includes(search.toLowerCase()) ? (
                                <div
                                    ref={(el) => (segmentRefs.current[i] = el)}
                                    key={i}
                                    className={`segment ${i === currentIndex ? "active" : ""}`}
                                    onClick={() => selectSegment(i)}
                                >
                                    <span style={{ minWidth: 28 }}>{i + 1}</span>
                                    <span>
                                        {seg.substring(0, 55)}
                                        {seg.length > 55 ? "..." : ""}
                                    </span>
                                </div>
                            ) : null
                        )}
                    </div>
                </div>

                {/* WORKSPACE */}
                <div className="workspace">
                    {/* SOURCE */}
                    <div className="pane">
                        <div className="pane-header">
                            Source {numStr}
                            <button
                                className="btn btn-edit"
                            // onClick={() => setIsSourceEditing(!isSourceEditing)}
                            >
                                {isSourceEditing ? "Done" : "Edit"}
                            </button>
                        </div>

                        <div className="pane-content">
                            {isSourceEditing ? (
                                <>
                                    <textarea
                                        className={`editor-box ${rtlSource ? "urdu-font" : ""}`}
                                        value={activeSource}
                                        onChange={(e) => updateSource(e.target.value)}
                                        dir={rtlSource ? "rtl" : "ltr"}
                                    />
                                    <div
                                        className={`preview-area ${rtlSource ? "urdu-font" : ""}`}
                                        dir={rtlSource ? "rtl" : "auto"}
                                        dangerouslySetInnerHTML={renderHTML(activeSource)}
                                    />
                                </>
                            ) : (
                                <div
                                    className={`preview-area ${rtlSource ? "urdu-font" : ""}`}
                                    dir={rtlSource ? "rtl" : "auto"}
                                    dangerouslySetInnerHTML={renderHTML(activeSource)}
                                />
                            )}
                        </div>
                    </div>

                    {/* TARGET – most important part for Urdu */}
                    <div className="pane">
                        <div className="pane-header text-sm">
                            Target {numStr}
                            <div className="space-x-2">
                                <button
                                    className="btn"
                                    onClick={copySourceToTarget}
                                    title="Copy Source Text"
                                >
                                    <FiCopy size={15} />
                                    {/* <span>Copy Source</span> */}
                                </button>
                                <button className="btn" onClick={goPrev}>Prev</button>
                                <button className="btn" onClick={goNext}>Next</button>
                                <button
                                    className="btn btn-edit"
                                    onClick={() => {
                                        if (isTargetEditing) {
                                            handleSave();
                                        } else {
                                            setIsTargetEditing(true);
                                        }
                                    }}
                                >
                                    {isTargetEditing ? "Save" : "Edit"}
                                </button>
                            </div>
                        </div>

                        <div className="pane-content">
                            {isTargetEditing ? (
                                <>
                                    <textarea
                                        ref={targetTextareaRef}
                                        className={`editor-box ${rtlTarget ? "urdu-font" : ""}`}
                                        value={activeTarget}
                                        onChange={(e) => updateTarget(e.target.value)}
                                        dir={rtlTarget ? "rtl" : "ltr"}
                                    />
                                    {/* Target preview */}
                                    <div
                                        className={`preview-area ${rtlTarget ? "urdu-font" : ""}`}
                                        dir={rtlTarget ? "rtl" : "auto"}
                                        dangerouslySetInnerHTML={renderHTML(activeTarget)}
                                    />
                                </>
                            ) : (
                                <div
                                    className={`preview-area ${rtlTarget ? "urdu-font" : ""}`}
                                    dir={rtlTarget ? "rtl" : "auto"}
                                    dangerouslySetInnerHTML={renderHTML(activeTarget)}
                                />
                            )}
                        </div>
                    </div>
                </div>
            </div>
            <BottomBar />
        </>
    );
};

export default EditorPanel;